<template>
  <div class="flex h-full w-full">
    <FirstColumn
      :legalText
      :legalTextParagraph
      :paragraphLoading
      :fieldOfLaw
      :activeEntrySlug
      :bookmarks
      :toc
      :nestedData
      :tocLoading
      :sortedNotes
      :parentsOfActiveParagraph
    />
    <!-- column 2 & 3 -->
    <MiddleColumn
      :legalText
      :legalTextParagraph
      :legalTextSlug
      :legalTextParagraphSlug
      :legalTextLoading
      :paragraphLoading
      :activeEntrySlug
      :activeEntryType
      :nextLegalTextParagraph
      :previousLegalTextParagraph
      :toc
      :nestedData
      :active
      :fieldOfLaw
    />
    <!-- column 4 -->
    <ExpansibleColumn
      v-if="!showcase"
      id="step-7"
      end
      class="flex-none py-1 max-lg:hidden sm:py-2"
    >
      <template #default="{ collapsed }">
        <ContentOverview
          :legalText="legalText"
          :legalTextParagraph="legalTextParagraph"
          :showEmptyNotes="Boolean(legalTextParagraphSlug)"
          :columnCollapsed="collapsed"
          :paragraphFol="fieldOfLaw"
        />
      </template>
    </ExpansibleColumn>

    <AlertModal />
  </div>
</template>

<script lang="ts" setup>
import type { Props } from "./props";

const AlertModal = defineAsyncComponent(
  () => import("~/components/legaltextparagraph/guest/AlertModal.vue")
);
const routeParams = useRouteParams<Props>();

const showcase = useExamShowcase();

const {
  legalTextSlug,
  legalTextParagraphSlug,
  activeEntryType,
  activeEntrySlug,
} = toRefs(routeParams);

const {
  active,
  enabled: activeEntryEnabled,
  query: { suspense: activeEntryQuerySuspense },
} = useFetchActiveEntry();

const legalTextQuery = useCurrentLegalText();
const { data: legalText, isLoading: legalTextLoading } = legalTextQuery;

const { bookmarks } = useBookmarks(legalText);
const {
  toc,
  nestedData,
  tocLoading,
  suspense: tocSuspense,
  parentsOfActiveParagraph,
  next: nextLegalTextParagraph,
  previous: previousLegalTextParagraph,
  enabled: fetchTocEnabled,
} = useToc(legalTextSlug, legalTextParagraphSlug);

const {
  query: {
    data: legalTextParagraph,
    isLoading: paragraphLoading,
    suspense: paragraphSuspense,
  },
  enabled: paragraphEnabled,
} = useCurrentParagraph();

const paragraphFolId = computed(
  () => legalTextParagraph.value?.field_of_law_primary_id ?? undefined
);

const { query: folQuery, enabled: folQueryEnabled } =
  useFetchFieldOfLawById(paragraphFolId);

const { data: fieldOfLaw } = folQuery;

const setConfig = useParagraphMeta(legalText, legalTextParagraph, active);
watchEffect(() => setConfig());

onServerPrefetch(async () => {
  await Promise.all([
    legalTextQuery.suspense(),
    fetchTocEnabled.value && tocSuspense(),
    activeEntryEnabled.value && activeEntryQuerySuspense(),
    paragraphEnabled.value && paragraphSuspense(),
  ]);
  if (folQueryEnabled.value) {
    await folQuery.suspense();
  }
  setConfig();
});

const { sortedNotes } = useParagraphNotes();
</script>

<style lang="postcss" global>
@import "splitpanes/dist/splitpanes.css";
@import "~/scss/shephered.scss";

.lexmea-splitter-panes {
  .splitpanes.default-theme {
    & > .splitpanes__pane {
      background-color: transparent;
    }

    &.splitpanes--vertical > .splitpanes__splitter {
      background-color: #ebeef1;
      width: 3px;
    }
  }

  .splitpanes__pane {
    overflow-x: visible;
  }
}
</style>
